// Helpers
import { to, toCamel } from '../../lib/helpers';
import { gaActions } from '../gaActionTypes';
import { bodyHasError } from '../../lib/validation/serverErrors';
import { ISO_FORMAT } from '../../lib/helpers';

// Requests
import {
  getAllAppsRequest,
  getMyAppsRequest,
  unlinkAppLinkRequest,
  lookupAppLinkRequest,
  verifyUserAuditNumberRequest,
} from '../../backend/agencyRequests';

// Actions
import { APP_TRANSACTION_IDS } from '../app/appActions';
import { openModal } from '../../reducers/modal/modalActions';
import { storeAlertInfo } from '../alert/alertActions';
import { pushMessageBanner } from '../messageBanner/messageBannerActions';
import { storeSelected } from './dlr/dlrActions';

// Vendors
import { DateTime } from 'luxon';

// Constants
export const AGENCY_STORE_ALL_APPS = '[AGENCY] Store All Apps';
export const AGENCY_STORE_MY_APPS = '[AGENCY] Store My Apps';
export const AGENCY_STORE_AGENCY = '[AGENCY] Store Agency';
export const AGENCY_STORE_LINK = '[AGENCY] Store Link';
export const AGENCY_REMOVE_LINK_BY_USERKEY = `[AGENCY] ${gaActions.click} Remove Link By Userkey`;
export const AGENCY_UPDATE_LINK_DATA = '[AGENCY] Update Links';
export const AGENCY_CLEAR_DATA = '[AGENCY] Clear Data';

export const getAllApps = () => async dispatch => {
  const [{ response, body }, err] = await to(dispatch(getAllAppsRequest()));
  if (err) return Promise.reject(err);
  dispatch({ type: AGENCY_STORE_ALL_APPS, payload: body.data });
  return Promise.resolve({ response, body });
};

export const getMyApps = () => async dispatch => {
  const [{ response, body }, err] = await to(dispatch(getMyAppsRequest()));
  if (err) return Promise.reject(err);
  dispatch({ type: AGENCY_STORE_MY_APPS, payload: body.data });
  return Promise.resolve({ response, body });
};

export const unlinkAppLink = (
  { userKey },
  linkKey,
  isAutoUnlink = false
) => async dispatch => {
  const [{ response, body }, err] = await to(
    dispatch(
      unlinkAppLinkRequest({
        userKey,
        linkKey: linkKey ? linkKey : '',
        isAutoUnlink,
      })
    )
  );

  if (err || bodyHasError(body)) {
    return Promise.reject(err);
  }

  return Promise.resolve({ response, body });
};

export const removeLinkFromState = link => dispatch =>
  dispatch({ type: AGENCY_REMOVE_LINK_BY_USERKEY, payload: link });

export const storeLink = link => dispatch =>
  dispatch({ type: AGENCY_STORE_LINK, payload: link });

export const lookupBasicLinkData = links => async dispatch => {
  const apps = [];
  links.forEach(link => {
    const toPush = {
      appId: link.appId,
      userKeys: [link.userKey],
    };
    if (apps.some(app => app.appId === toPush.appId)) {
      apps.find(app => app.appId === toPush.appId).userKeys.push(link.userKey);
    } else {
      apps.push({
        appId: link.appId,
        userKeys: [link.userKey],
      });
    }
  });

  const results = apps.map(async link => {
    let { appId, userKeys } = link;

    const [{ body }, error] = await to(
      dispatch(
        lookupAppLinkRequest({
          appId,
          userKeys,
          transactionId: APP_TRANSACTION_IDS.BASIC_INFO,
        })
      )
    );
    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3007'] })) {
      const dt = DateTime.now();
      const startTime = dt.toFormat(ISO_FORMAT);
      const endTime = dt.plus({ minutes: 1 }).toFormat(ISO_FORMAT);
      const messageId = dt.toMillis();

      dispatch(
        pushMessageBanner([
          {
            messageId,
            textEnglish: '{"type": "Alerts", "id": "LinkNotFound"}',
            textSpanish: '{"type": "Alerts", "id": "LinkNotFound"}',
            startTime,
            endTime,
            priority: '2',
            author: 'USER',
            valid: true,
            dismissable: true,
            headerMessage: true,
          },
        ])
      );
      return Promise.reject(error);
    }
    if (bodyHasError(body, { errorCodes: ['MGMW_EX_5001'] })) {
      dispatch(
        storeAlertInfo({
          type: 'warning',
          title: { file: 'Alerts', id: 'SorryTitle' },
          message: { file: 'Alerts', id: 'AgencyNotFound' },
          closeable: true,
        })
      );
      return Promise.reject(error);
    }
    if (error) return Promise.reject(error);

    if (bodyHasError(body, { errorCodes: ['MGMW_EX_5002'] })) {
      dispatch(
        storeAlertInfo({
          type: 'warning',
          title: { file: 'Alerts', id: 'SorryTitle' },
          message: { file: 'Alerts', id: 'ApplicationNotFound' },
          closeable: true,
        })
      );
      return Promise.reject(error);
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_EX_3014', 'MGMW_EX_4000'] })) {
      dispatch(openModal('VehicleNotFoundModal'));
      return Promise.reject(error);
    }

    if (error) return Promise.reject(error);

    // DPS lookup validation to resolve $0 subtotal issue
    if (appId === '9') {
      const profile = Array.isArray(body?.data?.profiles)
        ? body.data.profiles[0]
        : null;
      const linkedLicense = Array.isArray(profile?.account)
        ? profile.account[0]
        : null;
      if (linkedLicense) {
        dispatch(storeSelected(linkedLicense));
      }
    }

    const linkData = Array.isArray(body.data)
      ? { ...link, profiles: [...body.data] }
      : { ...link, ...body.data };

    const linkData2 = toCamel(linkData);

    return Promise.resolve(linkData2);
  });

  return Promise.all(results).then(updatedLinks => {
    dispatch({ type: AGENCY_UPDATE_LINK_DATA, payload: updatedLinks });
    return Promise.resolve(updatedLinks);
  });
};

export const lookupBasicLinkDataAndGetSelected = links => async (
  dispatch,
  getState
) => {
  const updatedLinks = await dispatch(lookupBasicLinkData(links));
  const selected = getState().dps && getState().dps.selected;
  return { updatedLinks, selected };
};

export const clearAgencyData = () => dispatch =>
  dispatch({ type: AGENCY_CLEAR_DATA });

export const verifyAuditNumber = data => async (dispatch, getState) => {
  const state = getState();

  const [{ response, body }, err] = await to(
    dispatch(verifyUserAuditNumberRequest({ ...data }))
  );

  if (!body) return Promise.reject();

  if (err) return Promise.reject(err);

  if (bodyHasError(body, { errorCodes: ['MGMW_EX_3700'] })) {
    dispatch(openModal('AuditLockoutModal'));
    return Promise.reject(err || response);
  }

  if (bodyHasError(body, { errorCodes: ['MGMW_EX_3007'] })) {
    // dispatch(openModal('AuditLockoutModal'));
    return Promise.reject(err || response);
  }

  return Promise.resolve({ response });
};
